import { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  DocumentStatus,
  InternalSidepanel,
  DocumentType,
  PermissionEnum,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  BlacklistDataContent,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  changePanelState,
  clearTransactionState,
  setBlacklistData,
  setBlacklistDataItem,
  setBlacklistDataVisible,
  setBlacklistItemVisible,
  setPanelIsOpen,
} from '../store/slices/internal-transaction';
import {
  useLazyGetTransactionAmlQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetCustomerInternalTransactionQuery,
  useLazyGetCustomerInternalTransactionWarningsQuery,
  useLazyGetProblemTransactionChatsQuery,
  useLazyGetProblemTransactionRecipientClientsOfficiasQuery,
  useLazyGetProblemTransactionSenderClientsOfficiasQuery,
  useLazyGetProblemTransactionsDocumentsQuery,
  useLazyGetProblemTransactionDocumentByIdQuery,
  useLazyGetProblemTransactionDocumentFileQuery,
  useDeleteProblemTransactionDocumentsMutation,
  useChangeProblemTransactionDocumentCategoryMutation,
  useChangeProblemTransactionDocumentStatusMutation,
  useAddCommentToProblemTransactionDocumentMutation,
  useDeleteCommentInProblemTransactionDocumentMutation,
  useEditCommentInProblemTransactionDocumentMutation,
} from '../store/services/customers';

const ProblemInternalTransactionHOC = ({
  matchPath,
  backPath,
  generalPath,
  amlPath,
  logPath,
  warningPath,
  changeLogPath,
  chatPath,
  documentPath,
  commentsPath,
}: {
  matchPath: string;
  backPath: string;
  generalPath: string;
  amlPath: string;
  logPath: string;
  warningPath: string;
  changeLogPath: string;
  chatPath: string;
  documentPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const match = useMatch(matchPath);

  const [imgLink, setImgLink] = useState('');

  const {
    customersProblemTransactions: { internal },
    customersDictionaries: { activePermissions, currenciesList },
  } = useAppSelector((state) => state);

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const { customerId, transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [getInternalAml, { isLoading: isLoadingAml }] =
    useLazyGetTransactionAmlQuery();

  const [getInternalLogs, { isLoading: isLoadingLog }] =
    useLazyGetCustomersTransactionLogsQuery();

  const [
    getCustomerInternalTransactionWarningsQuery,
    { isLoading: warningsIsLoading },
  ] = useLazyGetCustomerInternalTransactionWarningsQuery();
  const [getChats] = useLazyGetProblemTransactionChatsQuery();
  const [getSenderAccounts] =
    useLazyGetProblemTransactionSenderClientsOfficiasQuery();
  const [getRecipientAccounts] =
    useLazyGetProblemTransactionRecipientClientsOfficiasQuery();

  const [
    getCustomerInternalTransaction,
    { isLoading: transactionIsLoading, isFetching },
  ] = useLazyGetCustomerInternalTransactionQuery();

  const [getTransactionDocuments] =
    useLazyGetProblemTransactionsDocumentsQuery();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetProblemTransactionDocumentByIdQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetProblemTransactionDocumentFileQuery();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteProblemTransactionDocumentsMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeProblemTransactionDocumentCategoryMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeProblemTransactionDocumentStatusMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentToProblemTransactionDocumentMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInProblemTransactionDocumentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentInProblemTransactionDocumentMutation();

  const getInternalTransactionById = useCallback(() => {
    getCustomerInternalTransaction(transactionId as string);
    getInternalAml(transactionId as string);
    getCustomerInternalTransactionWarningsQuery(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_INTERNAL_LIST,
      )
    ) {
      getInternalLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }

    if (
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST)
    ) {
      getTransactionDocuments({ id: transactionId as string });
    }
  }, [transactionId, activePermissions]);

  const handleClose = () => {
    dispatch(setPanelIsOpen(false));

    setTimeout(() => {
      navigate(
        generatePath(`${backPath}${search || ''}`, {
          customerId,
        }),
      );
    }, 100);
  };

  const getTransactionDocumentyIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };
  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = internal?.documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: internal?.document?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  const handleCloseBlacklistData = () => {
    dispatch(setBlacklistDataVisible(false));
    dispatch(setBlacklistData(undefined));
    dispatch(setBlacklistItemVisible(false));
    dispatch(setBlacklistDataItem(undefined));
    dispatch(changePanelState({ state: true, transactionId }));
  };

  useEffect(() => {
    if (
      internal?.transaction &&
      internal?.transaction.senderAccount &&
      internal?.transaction.senderAccount?.organization?.type === 'CORPORATE'
    ) {
      getSenderAccounts(internal?.transaction.senderAccount.organizationId);
    }
  }, [internal?.transaction]);

  useEffect(() => {
    if (
      internal?.transaction &&
      internal?.transaction.recipientAccount &&
      internal?.transaction.recipientAccount?.organization?.type === 'CORPORATE'
    ) {
      getRecipientAccounts(
        internal?.transaction.recipientAccount.organizationId,
      );
    }
  }, [internal?.transaction]);

  useEffect(() => {
    dispatch(setPanelIsOpen(true));

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  useEffect(() => {
    if (customerId && !internal?.transaction && transactionId) {
      getInternalTransactionById();
    }
  }, [
    customerId,
    internal?.transaction,
    transactionId,
    getInternalTransactionById,
  ]);

  return (
    <>
      <InternalSidepanel
        panelIsOpen={internal.panelIsOpen}
        handleClose={handleClose}
        isLoading={
          transactionIsLoading ||
          isLoadingAml ||
          isLoadingLog ||
          warningsIsLoading ||
          isFetching
        }
        title="Internal Transaction"
        tabs={{
          warningPath: generatePath(`${warningPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          generalPath: generatePath(`${generalPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          amlPath: generatePath(`${amlPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          logPath: generatePath(`${logPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          chatPath: generatePath(`${chatPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          documentsPath: generatePath(`${documentPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          commentsPath: generatePath(`${commentsPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
        }}
        isWarning
        getTransactionDocument={getTransactionDocumentyIdAction}
        enableEdit={
          !activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
          )
        }
        isCreateForm={
          !activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
          )
        }
        createAllowed={activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
        )}
        document={internal.document}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        imgLink={imgLink}
        onDelete={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        onDownload={onDownloadDocument}
        sendComment={onSendDocumentComment}
        editComment={onEditComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        deleteComment={onDeleteComment}
        deleteCommentInProgress={deleteCommentInProgress}
        commentInProgress={commentInProgress}
        documentStatusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onDownloadSuccess={downloadFileIsSuccess}
        documentIsLoading={documentIsLoading || documentIsFetching}
        activePermission={activePermissions}
        currenciesList={currenciesList}
        transactionInfo={internal?.transaction}
        sendMessage={sendMessage}
        sidepanelExpandedKey="problem-internal"
      />
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={internal.blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={internal.blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-internal-blacklist"
      >
        <BlacklistDataContent data={internal.blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={internal.blacklistItemVisible}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-internal-blacklist"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={internal.blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default ProblemInternalTransactionHOC;
